import { cn } from '@kit/ui/utils';

/**
 * Das Kachelraster der 404 und der Fehlerseite — Blatt P7.
 *
 * ===========================================================================
 * 🔴 WARUM HIER KEIN RING MEHR DREHT
 * ===========================================================================
 *
 * P7 korrigiert sich an dieser Stelle selbst, und die Begründung gilt über
 * diese Seite hinaus:
 *
 *   „Hier stand ein endlos rotierender Ring — falsch. Ein drehender Kreis
 *    heisst «läuft noch». Auf einer Fehlerseite läuft nichts mehr; sie ist das
 *    Ergebnis. Ein Endlos-Ring lässt einen warten, wo nichts kommt — und man
 *    wartet tatsächlich, weil das Zeichen es sagt."
 *
 * ===========================================================================
 * ZWEI AUSSAGEN, EINE BAUFORM
 * ===========================================================================
 *
 *   `fehlt`     Die 404. Eine Kachel FEHLT und steht als gestrichelter Umriss.
 *               „Das ist die 404 in einem Bild: alles da, eines nicht."
 *               Das Raster atmet — 24 Kacheln, jede über 4 bis 6,8 Sekunden
 *               mit eigener Verzögerung auf 40 % Deckkraft und zurück.
 *
 *   `verdreht`  Die Fehlerseite. Dasselbe Raster, aber STILL — eine Kachel um
 *               14° verdreht und in Vermillion. „Die 404 sagt «eines ist nicht
 *               da», der Fehler sagt «eines ist schiefgegangen»."
 *
 * 🔴 Die versetzte Verzögerung ist kein Zierrat. P7: „die Versetzung verhindert
 * das Pulsieren im Gleichschritt, das billig aussieht" — und das Ganze bleibt
 * „ruhig genug, um daneben zu lesen".
 *
 * 🔴 `prefers-reduced-motion`: das Raster steht still, „die Lücke bleibt
 * sichtbar". Die Aussage hängt an der Lücke, nicht an der Bewegung — deshalb
 * verliert die Seite ohne Animation nichts.
 *
 * ⚠️ Die verdrehte Kachel ist die zweite Stelle, an der die Akzentfarbe einen
 * Zustand trägt (U4) — „hier zu Recht: die Kachel IST die Ausnahme". Das ist
 * kein Freibrief: U4 verbietet Akzent für den Zustand einer PERSON, nicht für
 * eine Kachel, die für sich selbst steht.
 */

/*
 * 🔴 Der Kachel-Radius kommt aus der A2-Skala, nicht aus dem Augenmass.
 *
 * Der erste Anlauf trug `rounded-[4px]` — eine vierte Stufe neben den drei
 * erlaubten, und `check:radius-skala` hat sie sofort gemeldet („drei Werte,
 * nicht sechs"). `rounded-lg` (8 px) ist der kleinste erlaubte Wert.
 *
 * ⚠️ HIER STAND, das Blatt zeichne die Kacheln „leicht gerundet, ohne eine
 * Zahl zu nennen". Die PROSA von P7 nennt keine — die ZEICHNUNG sehr wohl:
 * **3 px**, gemessen am gerenderten Blatt (21.09.2026) an allen drei
 * Kachelarten und in beiden Flaechen. Die Entscheidung fuer 8 bleibt richtig
 * (A2 gewinnt bei Widerspruch, und 3 steht auf keiner Skala); ihre
 * Begruendung war es nicht. Festgenagelt in check:masse (gelöscht mit T21), Proben 961–963.
 */

/** 6 × 4 = 24, wie das Blatt es zeichnet. */
const SPALTEN = 6;
const ZEILEN = 4;
const KACHELN = SPALTEN * ZEILEN;

/**
 * Welche Kachel die besondere ist.
 *
 * 🔴 Fest, nicht zufällig. `Math.random()` liefe auf Server und Browser
 * verschieden aus und würde bei jedem Rendern eine andere Kachel treffen — ein
 * Flackern beim Hydrieren, und in einem Bildschirmfoto nie dasselbe Bild.
 * Die 15 liegt in der dritten Reihe, mittig-rechts: weit genug vom Rand, um
 * als Lücke gelesen zu werden.
 */
const BESONDERE = 15;

export function FehlerRaster({ art }: { art: 'fehlt' | 'verdreht' }) {
  return (
    <div
      aria-hidden
      data-test={`fehler-raster-${art}`}
      className="grid w-fit gap-2"
      style={{ gridTemplateColumns: `repeat(${SPALTEN}, 22px)` }}
    >
      {Array.from({ length: KACHELN }, (_, i) => {
        const besonders = i === BESONDERE;

        /*
         * 4 bis 6,8 Sekunden, versetzt — die Werte stehen im Blatt. Sie hängen
         * am Index, damit Server und Browser dasselbe rechnen.
         */
        const dauer = 4 + (i % 8) * 0.4;
        const verzug = (i % 12) * 0.35;

        if (besonders && art === 'fehlt') {
          return (
            <span
              key={i}
              className="border-primary/40 h-5.5 w-5.5 rounded-lg border border-dashed"
            />
          );
        }

        if (besonders) {
          return (
            <span
              key={i}
              className="bg-emphasis h-5.5 w-5.5 rotate-14 rounded-lg"
            />
          );
        }

        return (
          <span
            key={i}
            className={cn(
              'bg-primary/[0.16] h-5.5 w-5.5 rounded-lg',
              /*
                Nur die 404 atmet. `motion-safe:` hält sie an, wenn der
                Betrachter weniger Bewegung wünscht — die Lücke bleibt.
              */
              art === 'fehlt' && 'motion-safe:animate-pulse',
            )}
            style={
              art === 'fehlt'
                ? {
                    animationDuration: `${dauer}s`,
                    animationDelay: `${verzug}s`,
                  }
                : undefined
            }
          />
        );
      })}
    </div>
  );
}
